import ThemedSelect from "../shared/ThemedSelect.jsx";
import { useEffect, useMemo, useRef, useState } from "react";
import { Button, Col, Form, Modal, Row } from "react-bootstrap";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import ClaimPowSchedule from "./ClaimPowSchedule.jsx";
import TabHeader from "./TabHeader.jsx";
import PrintHeader from "../shared/PrintHeader.jsx";
import "../../styles/calendar.css";
import {
  listPowTasks,
  createPowTask,
  updatePowTask,
  deletePowTask,
  listOrders,
} from "../../services/apiClient.js";
import { toInputDate } from "../../utils/date.js";

// POW (Program of Works) — the claim's schedule: dated tasks assigned to a
// contractor, shown on a calendar (legacy ProgramOfWorks.aspx + FullCalendar).
// Click a day to add a task, click an event to edit, drag it to reschedule.
// "Display By" controls the event label; "Hide completed" filters. Contractor
// options come from the claim's Orders (plus any already on a task so imported
// tasks keep their name). Beside the calendar sits the separate "Schedule"
// checklist (ClaimPowSchedule). Complementary to Orders: "when work happens".

const EMPTY = { title: "", description: "", assigned_date: "", contractor_company_id: "", is_complete: false };

const DISPLAY_OPTIONS = [
  { value: "title", label: "Title" },
  { value: "contractortitle", label: "Contractor - Title" },
  { value: "description", label: "Description" },
  { value: "all", label: "Title - Description" },
];

function ClaimPowTab({ claimId, claim = null }) {
  const [tasks, setTasks] = useState([]);
  const [orders, setOrders] = useState([]);
  const [hideCompleted, setHideCompleted] = useState(false);
  const [displayOption, setDisplayOption] = useState("contractortitle");
  const [error, setError] = useState("");

  // Modal: mode is "new" | "edit" | null. editId holds the task being edited.
  const [mode, setMode] = useState(null);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);

  // Extended date range (legacy "Extended Date Range"): a dayGrid view spanning N
  // weeks from a chosen start, instead of the fixed month grid.
  const [extended, setExtended] = useState(false);
  const [rangeStart, setRangeStart] = useState("");
  const [rangeEnd, setRangeEnd] = useState("");
  const [rangeWeeks, setRangeWeeks] = useState(6);

  const calRef = useRef(null);
  const calendarShell = useRef(null);
  useEffect(() => {
    const shell = calendarShell.current;
    if (!shell) return;
    // FullCalendar owns its toolbar markup. Adapt only those generated titles;
    // the rest of the app authors data-hint directly (no whole-page title scan).
    const syncHints = () => {
      shell.querySelectorAll(".fc-toolbar button[title]").forEach((button) => {
        const label = button.getAttribute("title");
        if (label) {
          button.setAttribute("data-hint", label);
          button.setAttribute("aria-label", label);
        }
        button.removeAttribute("title");
      });
    };
    syncHints();
    const observer = new MutationObserver(syncHints);
    observer.observe(shell, { childList: true, subtree: true, attributes: true, attributeFilter: ["title"] });
    return () => observer.disconnect();
  }, []);
  const dontAskMove = useRef(false);

  function load() {
    return listPowTasks(claimId, false)
      .then(setTasks)
      .catch(() => setError("Unable to load tasks."));
  }
  useEffect(() => {
    load();
    listOrders(claimId).then(setOrders).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claimId]);

  // Contractor options: the distinct contractors from this claim's orders, plus any
  // already on a task (so imported tasks keep their contractor in the dropdown).
  const contractors = useMemo(() => {
    const m = new Map();
    for (const o of orders) if (o.contractor_company_id && o.contractor_name) m.set(String(o.contractor_company_id), o.contractor_name);
    for (const t of tasks) if (t.contractor_company_id && t.contractor_name) m.set(String(t.contractor_company_id), t.contractor_name);
    return [...m.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [orders, tasks]);

  const visibleTasks = useMemo(
    () => (hideCompleted ? tasks.filter((t) => !t.is_complete) : tasks),
    [tasks, hideCompleted]
  );

  // Calendar events — only dated tasks land on the grid (undated ones still show in
  // the list). Completed tasks get a class for muted styling.
  const events = useMemo(
    () =>
      visibleTasks
        .filter((t) => t.assigned_date)
        .map((t) => ({
          id: String(t.id),
          title: t.title || "(untitled)",
          start: toInputDate(t.assigned_date),
          allDay: true,
          classNames: t.is_complete ? ["pow-complete"] : [],
          extendedProps: {
            description: t.description || "",
            contractorName: t.contractor_name || "",
            isComplete: !!t.is_complete,
          },
        })),
    [visibleTasks]
  );

  const set = (patch) => setForm((c) => ({ ...c, ...patch }));

  function openNew(assignedDate) {
    setError("");
    setForm({ ...EMPTY, assigned_date: assignedDate || "" });
    setEditId(null);
    setMode("new");
  }
  function openEdit(t) {
    setError("");
    setForm({
      title: t.title || "",
      description: t.description || "",
      assigned_date: toInputDate(t.assigned_date) || "",
      contractor_company_id: t.contractor_company_id || "",
      is_complete: !!t.is_complete,
    });
    setEditId(t.id);
    setMode("edit");
  }
  const closeModal = () => setMode(null);

  async function save() {
    if (!form.title.trim()) {
      setError("Task title is required.");
      return;
    }
    setError("");
    try {
      if (mode === "new") await createPowTask(claimId, form);
      else await updatePowTask(claimId, editId, form);
      await load();
      closeModal();
    } catch (err) {
      setError(err.response?.data?.error || "Unable to save task.");
    }
  }
  async function remove(id = editId) {
    if (!window.confirm("Delete this task?")) return;
    try {
      await deletePowTask(claimId, id);
      await load();
      closeModal();
    } catch (err) {
      setError(err.response?.data?.error || "Unable to delete task.");
    }
  }
  // Drag-to-reschedule: persist the new date, revert the event on cancel/error.
  async function moveEvent(info) {
    if (!dontAskMove.current && !window.confirm("Move this task to the new date?")) {
      info.revert();
      return;
    }
    dontAskMove.current = true;
    const t = tasks.find((x) => String(x.id) === String(info.event.id));
    if (!t) {
      info.revert();
      return;
    }
    try {
      await updatePowTask(claimId, t.id, {
        title: t.title,
        description: t.description,
        assigned_date: toInputDate(info.event.startStr),
        contractor_company_id: t.contractor_company_id,
        is_complete: t.is_complete,
      });
      await load();
    } catch (err) {
      setError(err.response?.data?.error || "Unable to move task.");
      info.revert();
    }
  }

  function renderEventContent(arg) {
    const { description, contractorName } = arg.event.extendedProps;
    const title = arg.event.title;
    let text = title;
    if (displayOption === "description") text = description || title;
    else if (displayOption === "all") text = description ? `${title} — ${description}` : title;
    else if (displayOption === "contractortitle") text = contractorName ? `${contractorName} - ${title}` : title;
    return <div data-hint={description}>{text}</div>;
  }

  function applyRange() {
    if (!rangeStart || !rangeEnd) {
      setError("Pick both a start and an end date for the extended range.");
      return;
    }
    const start = new Date(rangeStart);
    const end = new Date(rangeEnd);
    const days = Math.round((end - start) / 86400000) + 1;
    const weeks = Math.max(1, Math.ceil(days / 7));
    setError("");
    setRangeWeeks(weeks);
    // Defer so the views prop (duration) updates before we switch into it.
    setTimeout(() => calRef.current?.getApi().changeView("powRange", rangeStart), 0);
  }
  function changeExtended(checked) {
    setExtended(checked);
    if (!checked) calRef.current?.getApi().changeView("dayGridMonth");
  }

  const contractorSelect = (
    <ThemedSelect size="sm" value={form.contractor_company_id} onChange={(e) => set({ contractor_company_id: e.target.value })}>
      <option value="">— None —</option>
      {contractors.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
    </ThemedSelect>
  );

  return (
    <>
      <TabHeader
        title="Program of Works"
        help="pow"
        className="no-print"
        actions={
          <>
            <Form.Check type="checkbox" id="pow-hide-done" label="Hide completed tasks" checked={hideCompleted} onChange={(e) => setHideCompleted(e.target.checked)} />
            <Button size="sm" onClick={() => openNew("")}>+ New Task</Button>
          </>
        }
      />

      {error && mode === null ? <div className="alert alert-danger no-print">{error}</div> : null}

      <Row className="g-4">
        <Col xs={12} xl={8}>
          {/* ONE control row, three groups: Print header · Display by ·
              Extended date range (Robert 2026-08-11 — it was two rows and the
              second was mostly empty). gap-4 rather than gap-3 is the padding
              between the first two he asked for; without it "Print header" and
              "Display by:" read as one label. */}
          {/* no-print sits on the GROUPS below, never on this row. PrintHeader
              renders the printed document header as a sibling of its own
              controls, so a `no-print` here would hide that too — display:none
              on an ancestor is not something a child can escape, and the header
              silently stopped printing when it moved into this row (Robert
              2026-08-11).
              d-print-block for the same reason: on paper the only surviving
              child is that document header, and left as a flex ROW it would be
              sized as a flex item rather than filling the page width. */}
          <div className="d-flex d-print-block flex-wrap align-items-center gap-4 mb-2 position-relative">
            <PrintHeader
              className=""
              defaultTitle={[
                `POW Calendar`,
                [claim?.claim_number ? `#${claim.claim_number}` : "", claim?.client_name]
                  .filter(Boolean)
                  .join(" · "),
                claim?.street_address,
              ]
                .filter(Boolean)
                .join(" : ")}
              noteLabel="Note to the contractor (optional) — prints under the title"
            />
            <div className="d-flex align-items-center gap-2 no-print">
              <span className="small fw-semibold">Display by:</span>
              {DISPLAY_OPTIONS.map((o) => (
                <Form.Check
                  key={o.value}
                  inline
                  type="radio"
                  name="pow-display"
                  id={`pow-display-${o.value}`}
                  label={o.label}
                  className="small"
                  checked={displayOption === o.value}
                  onChange={() => setDisplayOption(o.value)}
                />
              ))}
            </div>
            <div className="d-flex align-items-center gap-2 ms-auto no-print">
              <Form.Check type="checkbox" id="pow-extended" label="Extended date range" checked={extended} onChange={(e) => changeExtended(e.target.checked)} />
              {extended ? (
                <>
                  <Form.Control size="sm" type="date" style={{ width: 150 }} value={rangeStart} onChange={(e) => setRangeStart(e.target.value)} />
                  <span className="small">to</span>
                  <Form.Control size="sm" type="date" style={{ width: 150 }} value={rangeEnd} onChange={(e) => setRangeEnd(e.target.value)} />
                  <Button size="sm" variant="outline-secondary" onClick={applyRange}>Apply</Button>
                </>
              ) : null}
            </div>
          </div>

          {/* NOT id="print-area" — AppLayout already puts that id on <main>, and
              two elements sharing an id is invalid HTML that print.css's
              `#root #print-area` then matches TWICE, running its print strategy
              against both the page shell and this calendar at once. Found
              2026-08-11 while chasing the POW print complaint; the calendar
              prints correctly as ordinary content inside the one real
              print-area. */}
          <div className="pow-print" ref={calendarShell}>
            <FullCalendar
              ref={calRef}
              plugins={[dayGridPlugin, interactionPlugin]}
              initialView="dayGridMonth"
              headerToolbar={{ left: "prev,next today", center: "title", right: "dayGridMonth,powRange" }}
              views={{ powRange: { type: "dayGrid", duration: { weeks: rangeWeeks }, buttonText: "Extended Range" } }}
              weekends={false}
              editable
              selectable
              dayMaxEvents={false}
              contentHeight="auto"
              events={events}
              eventContent={renderEventContent}
              dateClick={(info) => openNew(info.dateStr)}
              eventClick={(info) => {
                const t = tasks.find((x) => String(x.id) === String(info.event.id));
                if (t) openEdit(t);
              }}
              eventDrop={moveEvent}
            />
          </div>
        </Col>

        {/* NO-PRINT, deliberately. 1.0 prints the calendar and nothing else —
            its PrintPage only ever received the calendar — so a printed POW
            matches what the office has sent contractors for twenty years
            (Robert 2026-08-11: "I don't think in 1.0 there is any way to print
            the schedule list, it was just the calendar"). The checklist is an
            internal tracker, not part of the contractor's document. */}
        <Col xs={12} xl={4} className="no-print">
          <ClaimPowSchedule claimId={claimId} />
        </Col>
      </Row>

      <Modal show={mode !== null} onHide={closeModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>{mode === "edit" ? "Edit Task" : "New Task"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && mode !== null ? <div className="alert alert-danger">{error}</div> : null}
          <Row className="g-3">
            <Col xs={6}>
              <Form.Label className="mb-0 small fw-semibold">Date</Form.Label>
              <Form.Control size="sm" type="date" value={form.assigned_date} onChange={(e) => set({ assigned_date: e.target.value })} />
            </Col>
            <Col xs={6}>
              <Form.Label className="mb-0 small fw-semibold">Contractor</Form.Label>
              {contractorSelect}
            </Col>
            <Col xs={12}>
              <Form.Label className="mb-0 small fw-semibold">Title (client)</Form.Label>
              <Form.Control size="sm" autoFocus value={form.title} onChange={(e) => set({ title: e.target.value })} />
            </Col>
            <Col xs={12}>
              <Form.Label className="mb-0 small fw-semibold">Description (PCO)</Form.Label>
              <Form.Control as="textarea" rows={3} size="sm" value={form.description} onChange={(e) => set({ description: e.target.value })} />
            </Col>
            <Col xs={12}>
              <Form.Check type="checkbox" id="pow-modal-done" label="Mark as complete" checked={form.is_complete} onChange={(e) => set({ is_complete: e.target.checked })} />
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          {mode === "edit" ? <Button variant="outline-danger" className="me-auto" onClick={() => remove(editId)}>Delete</Button> : null}
          <Button variant="secondary" onClick={closeModal}>Cancel</Button>
          <Button onClick={save}>{mode === "edit" ? "Save" : "Add Task"}</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default ClaimPowTab;
